import mondayApi from './mondayApiStandalone.js';

// ============================================================================
// QUERY FUNCTIONS (Read Operations)
// ============================================================================

/**
 * Get columns for one or more boards
 */
export async function getBoardColumns({ boardIds }) {
  const query = `
    query GetBoardColumns($boardIds: [ID!]!) {
      boards(ids: $boardIds) {
        id
        columns {
          id
          title
          type
        }
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardIds: boardIds.map(id => String(id)),
    });
    return response?.boards || [];
  } catch (err) {
    console.error("Error fetching board columns:", err);
    throw err;
  }
}

/**
 * Get dropdown column settings for a single column (for extracting dropdown options)
 * For multiple columns, use getDropdownColumns instead
 */
export async function getDropdownColumn({ boardId, columnId }) {
  const result = await getDropdownColumns({ 
    boardColumnPairs: [{ boardId, columnId }] 
  });
  return result[0] || null;
}

/**
 * Get dropdown column settings for multiple columns across multiple boards (batched)
 * This optimizes multiple getDropdownColumn calls into a single query
 * @param {Array<{boardId: string|number, columnId: string}>} boardColumnPairs - Array of {boardId, columnId} pairs
 * @returns {Array} Array of column objects (or null if not found) in the same order as boardColumnPairs
 */
export async function getDropdownColumns({ boardColumnPairs }) {
  if (!boardColumnPairs || boardColumnPairs.length === 0) {
    return [];
  }
  
  // Get unique board IDs
  const uniqueBoardIds = [...new Set(boardColumnPairs.map(pair => String(pair.boardId)))];
  
  const query = `
    query GetDropdownColumns($boardIds: [ID!]!) {
      boards(ids: $boardIds) {
        id
        columns {
          id
          type
          settings_str
        }
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardIds: uniqueBoardIds,
    });
    
    const boards = response?.boards || [];
    
    // Create a map of boardId -> columns for quick lookup
    const boardColumnsMap = {};
    for (const board of boards) {
      boardColumnsMap[String(board.id)] = board.columns || [];
    }
    
    // Map each requested pair to its column result
    const results = boardColumnPairs.map(({ boardId, columnId }) => {
      const columns = boardColumnsMap[String(boardId)] || [];
      const column = columns.find(col => col.id === columnId);
      return column || null;
    });
    
    return results;
  } catch (err) {
    console.error("Error fetching dropdown columns:", err);
    throw err;
  }
}

/**
 * Get properties from Properties board with optional column values
 * This combines getProperties and getPropertiesWithKeys into a single optimized query
 * @param {string} boardId - The Properties board ID
 * @param {string} keyColumnId - (Legacy) Single column ID to fetch
 * @param {string[]} columnIds - Array of column IDs to fetch
 */
export async function getProperties({ boardId, keyColumnId = null, columnIds = null }) {
  // Support both keyColumnId (legacy) and columnIds (new)
  const columnIdsToFetch = columnIds || (keyColumnId ? [keyColumnId] : null);
  const includeColumnValues = columnIdsToFetch !== null && columnIdsToFetch.length > 0;
  
  const query = includeColumnValues
    ? `
      query GetProperties($boardIds: [ID!]!, $columnIds: [String!]!) {
        boards(ids: $boardIds) {
          items_page(limit: 500) {
            items {
              id
              name
              column_values(ids: $columnIds) {
                id
                text
                value
              }
            }
          }
        }
      }
    `
    : `
      query GetProperties($boardIds: [ID!]!) {
        boards(ids: $boardIds) {
          items_page(limit: 500) {
            items {
              id
              name
            }
          }
        }
      }
    `;

  try {
    const variables = includeColumnValues
      ? {
          boardIds: [String(boardId)],
          columnIds: Array.isArray(columnIdsToFetch) ? columnIdsToFetch : [columnIdsToFetch],
        }
      : {
          boardIds: [String(boardId)],
        };
    
    const response = await mondayApi.makeRequest(query, variables);
    const board = response?.boards?.[0];
    return board?.items_page?.items || [];
  } catch (err) {
    console.error("Error fetching properties:", err);
    throw err;
  }
}

/**
 * Get properties with their Key # column values
 * @deprecated Use getProperties({ boardId, keyColumnId }) instead
 */
export async function getPropertiesWithKeys({ boardId, keyColumnId }) {
  return getProperties({ boardId, keyColumnId });
}

/**
 * Get board items with specified columns and linked items
 */
export async function getBoardItems({ boardIds, columnIds, linkToItemColumnId, linkedBoardId, queryAllColumns = false, nestedLinkToItemColumnId = null, nestedLinkedBoardId = null }) {
  // If linkToItemColumnId is provided, query linked_items
  // linkedBoardId is optional - if not provided, Monday.com will use the board relation to determine the linked board
  const includeLinkedItems = !!linkToItemColumnId;
  const includeNestedLinkedItems = !!nestedLinkToItemColumnId;
  
  // For mirror columns to work, we might need to query all columns
  // If queryAllColumns is true, don't filter by columnIds - this helps mirror columns populate
  const columnValuesQuery = queryAllColumns 
    ? `column_values { id text value type }`
    : `column_values(ids: $columnIds) { id text value type }`;
  
  // Build query based on whether we need linked items and whether we're querying all columns
  let query;
    if (includeLinkedItems) {
      // Build linked_items query - linkedBoardId is optional
      const linkedItemsQuery = linkedBoardId
        ? `linked_items(link_to_item_column_id: $linkToItemColumnId, linked_board_id: $linkedBoardId)`
        : `linked_items(link_to_item_column_id: $linkToItemColumnId)`;
      
      // Build nested linked_items query if needed (for Properties from BOOKINGS items)
      const nestedLinkedItemsQuery = includeNestedLinkedItems
        ? nestedLinkedBoardId
          ? `linked_items(link_to_item_column_id: $nestedLinkToItemColumnId, linked_board_id: $nestedLinkedBoardId)`
          : `linked_items(link_to_item_column_id: $nestedLinkToItemColumnId)`
        : '';
      
      // Build the nested linked_items section if needed
      const nestedLinkedItemsSection = nestedLinkedItemsQuery
        ? `
                    ${nestedLinkedItemsQuery} {
                      id
                      name
                    }
                  `
        : '';
      
      if (queryAllColumns) {
        const queryVars = linkedBoardId 
          ? `$boardIds: [ID!]!, $linkToItemColumnId: String!, $linkedBoardId: ID!${includeNestedLinkedItems ? (nestedLinkedBoardId ? ', $nestedLinkToItemColumnId: String!, $nestedLinkedBoardId: ID!' : ', $nestedLinkToItemColumnId: String!') : ''}`
          : `$boardIds: [ID!]!, $linkToItemColumnId: String!${includeNestedLinkedItems ? (nestedLinkedBoardId ? ', $nestedLinkToItemColumnId: String!, $nestedLinkedBoardId: ID!' : ', $nestedLinkToItemColumnId: String!') : ''}`;
        query = `
          query GetBoardItems(${queryVars}) {
            boards(ids: $boardIds) {
              id
              items_page(limit: 500) {
                items {
                  id
                  name
                  ${columnValuesQuery}
                  ${linkedItemsQuery} {
                    id
                    name
                    column_values {
                      id
                      text
                      value
                      type
                    }${nestedLinkedItemsSection}
                  }
                  subitems {
                    id
                    name
                  }
                }
              }
            }
          }
        `;
      } else {
        const queryVars = linkedBoardId
          ? `$boardIds: [ID!]!, $columnIds: [String!]!, $linkToItemColumnId: String!, $linkedBoardId: ID!${includeNestedLinkedItems ? (nestedLinkedBoardId ? ', $nestedLinkToItemColumnId: String!, $nestedLinkedBoardId: ID!' : ', $nestedLinkToItemColumnId: String!') : ''}`
          : `$boardIds: [ID!]!, $columnIds: [String!]!, $linkToItemColumnId: String!${includeNestedLinkedItems ? (nestedLinkedBoardId ? ', $nestedLinkToItemColumnId: String!, $nestedLinkedBoardId: ID!' : ', $nestedLinkToItemColumnId: String!') : ''}`;
        query = `
          query GetBoardItems(${queryVars}) {
            boards(ids: $boardIds) {
              id
              items_page(limit: 500) {
                items {
                  id
                  name
                  ${columnValuesQuery}
                  ${linkedItemsQuery} {
                    id
                    name
                    column_values {
                      id
                      text
                      value
                      type
                    }${nestedLinkedItemsSection}
                  }
                  subitems {
                    id
                    name
                  }
                }
              }
            }
          }
        `;
      }
  } else {
    if (queryAllColumns) {
      query = `
        query GetBoardItems($boardIds: [ID!]!) {
          boards(ids: $boardIds) {
            id
            items_page(limit: 500) {
              items {
                id
                name
                ${columnValuesQuery}
                subitems {
                  id
                  name
                }
              }
            }
          }
        }
      `;
    } else {
      query = `
        query GetBoardItems($boardIds: [ID!]!, $columnIds: [String!]!) {
          boards(ids: $boardIds) {
            id
            items_page(limit: 500) {
              items {
                id
                name
                ${columnValuesQuery}
                subitems {
                  id
                  name
                }
              }
            }
          }
        }
      `;
    }
  }

  try {
    const variables = {
      boardIds: boardIds.map(id => String(id)),
    };
    
    // Only include columnIds if we're not querying all columns
    if (!queryAllColumns) {
      variables.columnIds = columnIds;
    }
    
    if (includeLinkedItems) {
      variables.linkToItemColumnId = linkToItemColumnId;
      if (linkedBoardId) {
        variables.linkedBoardId = linkedBoardId;
      }
    }
    
    if (includeNestedLinkedItems) {
      variables.nestedLinkToItemColumnId = nestedLinkToItemColumnId;
      if (nestedLinkedBoardId) {
        variables.nestedLinkedBoardId = nestedLinkedBoardId;
      }
    }
    
    const response = await mondayApi.makeRequest(query, variables);
    return response?.boards || [];
  } catch (err) {
    console.error("Error fetching board items:", err);
    throw err;
  }
}

/**
 * Get Meter Readings tasks (used when updating after Weekly Checks completion)
 * @deprecated Use getBoardItems instead - they are functionally identical
 */
export async function getMeterReadingsTasks({ boardId, columnIds, linkToItemColumnId, linkedBoardId }) {
  const boards = await getBoardItems({
    boardIds: [boardId],
    columnIds: columnIds,
    linkToItemColumnId: linkToItemColumnId,
    linkedBoardId: linkedBoardId,
  });
  const board = boards?.[0];
  return board?.items_page?.items || [];
}

/**
 * Fetch a single item by ID from a board with specified columns
 * Also fetches linked items if linkToItemColumnId is provided
 * Uses getBoardItems and filters for the specific item ID
 * @param {Object} params
 * @param {string|number} params.boardId - The board ID
 * @param {string} params.itemId - The item ID to fetch
 * @param {string[]} params.columnIds - Array of column IDs to fetch
 * @param {string} params.linkToItemColumnId - Optional column ID for linked items (e.g., property column)
 * @param {string|number} params.linkedBoardId - Optional linked board ID
 * @returns {Object|null} The item object or null if not found
 */
export async function getItemById({ boardId, itemId, columnIds, linkToItemColumnId = null, linkedBoardId = null }) {
  if (!boardId || !itemId || !columnIds || columnIds.length === 0) {
    return null;
  }

  try {
    // Use getBoardItems and filter for the specific item
    // Include linked items if linkToItemColumnId is provided
    const boards = await getBoardItems({
      boardIds: [boardId],
      columnIds: columnIds,
      linkToItemColumnId: linkToItemColumnId || undefined,
      linkedBoardId: linkedBoardId || undefined,
    });

    const board = boards?.[0];
    const items = board?.items_page?.items || [];
    
    // Find the specific item by ID
    const item = items.find(item => String(item.id) === String(itemId));
    return item || null;
  } catch (err) {
    console.error("Error fetching item by ID:", err);
    throw err;
  }
}

// ============================================================================
// MUTATION FUNCTIONS (Write Operations)
// ============================================================================

/**
 * Create a new item in a board
 */
export async function createItem({ boardId, itemName, columnValues }) {
  const query = `
    mutation CreateItem($boardId: ID!, $itemName: String!, $columnValues: JSON!) {
      create_item(
        board_id: $boardId,
        item_name: $itemName,
        column_values: $columnValues
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemName: itemName,
      columnValues: JSON.stringify(columnValues),
    });
    return response?.create_item;
  } catch (err) {
    console.error("Error creating item:", err);
    throw err;
  }
}

/**
 * Add file to a column value
 */
export async function addFileToColumn({ itemId, columnId, file }) {
  try {
    return await mondayApi.uploadFile(itemId, columnId, file);
  } catch (err) {
    console.error("Error adding file to column:", err);
    throw err;
  }
}

/**
 * Clear all files from a file column
 */
export async function clearFileColumn({ boardId, itemId, columnId }) {
  if (!boardId || !itemId || !columnId) return;

  // For file columns, clearing can be done by setting an empty files array
  // via change_column_value. This removes all existing files from the column.
  const clearValue = { files: [] };

  const query = `
    mutation ClearFileColumn($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
      value: JSON.stringify(clearValue),
    });
    return response;
  } catch (err) {
    console.error("Error clearing file column:", err);
    throw err;
  }
}

/**
 * Update status column value
 */
export async function updateStatus({ boardId, itemId, statusColumnId, done }) {
  if (!boardId || !itemId || !statusColumnId) return;

  // For status columns, use change_column_value with JSON format
  // Status value format: {"label": "Done"} as a JSON string
  const statusValue = done ? { label: "Done" } : { label: "" };

  const query = `
    mutation ChangeStatus($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: statusColumnId,
      value: JSON.stringify(statusValue),
    });
    return response;
  } catch (err) {
    console.error("Error updating status:", err);
    throw err;
  }
}

/**
 * Delete an item from a board
 */
export async function deleteItem({ itemId }) {
  if (!itemId) return;

  try {
    // Monday.com API expects ID type (string), not Int
    const query = `
      mutation DeleteItem($itemId: ID!) {
        delete_item(item_id: $itemId) {
          id
        }
      }
    `;

    const response = await mondayApi.makeRequest(query, {
      itemId: String(itemId),
    });
    return response;
  } catch (err) {
    console.error("Error deleting item:", err);
    throw err;
  }
}

/**
 * Update item name
 */
export async function updateItemName({ boardId, itemId, name }) {
  if (!boardId || !itemId || !name) return;

  try {
    // Monday.com requires board_id for change_simple_column_value
    const query = `
      mutation ChangeItemName($boardId: ID!, $itemId: ID!, $name: String!) {
        change_simple_column_value(
          board_id: $boardId,
          item_id: $itemId,
          column_id: "name",
          value: $name
        ) {
          id
        }
      }
    `;

    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      name: name,
    });
    return response;
  } catch (err) {
    console.error("Error updating item name:", err);
    throw err;
  }
}

/**
 * Update dropdown column value
 */
export async function updateDropdownColumn({ boardId, itemId, columnId, labels }) {
  if (!boardId || !itemId || !columnId) return;

  // For dropdown columns, Monday.com expects { labels: [...] } format
  const dropdownValue = { labels: Array.isArray(labels) ? labels : labels ? [labels] : [] };

  const query = `
    mutation ChangeDropdownColumn($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
      value: JSON.stringify(dropdownValue),
    });
    return response;
  } catch (err) {
    console.error("Error updating dropdown column:", err);
    throw err;
  }
}

/**
 * Update status/color column value (for Time Tracking)
 */
export async function updateStatusColumn({ boardId, itemId, columnId, label }) {
  if (!boardId || !itemId || !columnId) return;

  // For status/color columns, Monday.com expects { label: "Yes" } or { label: "No" }
  const statusValue = { label: label || "" };

  const query = `
    mutation ChangeStatusColumn($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
      value: JSON.stringify(statusValue),
    });
    return response;
  } catch (err) {
    console.error("Error updating status column:", err);
    throw err;
  }
}

/**
 * Update text column value
 */
export async function updateTextColumn({ boardId, itemId, columnId, value }) {
  if (!boardId || !itemId || !columnId) return;
  
  // For simple text columns, Monday.com expects just a plain string value
  // Use change_simple_column_value mutation for text columns
  const textValue = value !== "" && value !== null ? value : "";

  const query = `
    mutation ChangeSimpleColumnValue($boardId: ID!, $itemId: ID!, $columnId: String!, $value: String!) {
      change_simple_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
      value: textValue,
    });
    return response;
  } catch (err) {
    console.error("Error updating text column:", err);
    throw err;
  }
}

/**
 * Update date column value
 */
export async function updateDateColumn({ boardId, itemId, columnId, date }) {
  if (!boardId || !itemId || !columnId) return;
  
  // If date is null or undefined, clear the date column
  if (!date) {
    return clearDateColumn({ boardId, itemId, columnId });
  }
  
  // For date columns, Monday.com expects date in format: {"date": "YYYY-MM-DD"}
  // Date should be in YYYY-MM-DD format
  const dateStr = date instanceof Date 
    ? date.toISOString().split('T')[0]
    : typeof date === 'string' 
      ? date.split('T')[0] 
      : null;
  
  if (!dateStr) return;

  const dateValue = { date: dateStr };

  const query = `
    mutation ChangeDateColumn($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
      change_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: $value
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
      value: JSON.stringify(dateValue),
    });
    return response;
  } catch (err) {
    console.error("Error updating date column:", err);
    throw err;
  }
}

export async function clearDateColumn({ boardId, itemId, columnId }) {
  if (!boardId || !itemId || !columnId) return;
  
  // To clear a date column in Monday.com, we need to pass an empty value
  // Using change_simple_column_value with empty string should clear it
  const query = `
    mutation ClearDateColumn($boardId: ID!, $itemId: ID!, $columnId: String!) {
      change_simple_column_value(
        board_id: $boardId,
        item_id: $itemId,
        column_id: $columnId,
        value: ""
      ) {
        id
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      boardId: String(boardId),
      itemId: String(itemId),
      columnId: columnId,
    });
    return response;
  } catch (err) {
    console.error("Error clearing date column:", err);
    throw err;
  }
}

/**
 * Create a subitem (subtask) for a parent item
 */
export async function createSubitem({ itemId, subitemName, columnValues = {} }) {
  if (!itemId || !subitemName) return;

  const query = `
    mutation CreateSubitem($itemId: ID!, $subitemName: String!, $columnValues: JSON) {
      create_subitem(
        parent_item_id: $itemId,
        item_name: $subitemName,
        column_values: $columnValues
      ) {
        id
        name
        column_values {
          id
          text
          value
        }
      }
    }
  `;

  try {
    const response = await mondayApi.makeRequest(query, {
      itemId: String(itemId),
      subitemName: subitemName,
      columnValues: Object.keys(columnValues).length > 0 ? JSON.stringify(columnValues) : null,
    });
    return response?.create_subitem;
  } catch (err) {
    console.error("Error creating subitem:", err);
    throw err;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Export mondayApi instance for use in other services
export { mondayApi as monday };